import React, { memo, useCallback, useMemo, useState } from 'react';
import { Typography, Button, Dropdown, message } from 'antd';
import type { MenuProps } from 'antd';
import { Share } from 'refly-icons';
import { LuLayoutGrid } from 'react-icons/lu';
import { NodeIcon } from '@refly-packages/ai-workspace-common/components/canvas/nodes/shared/node-icon';
import { FilePreview } from '@refly-packages/ai-workspace-common/components/canvas/canvas-resources/file-preview';
import { DriveFile, ResourceType, EntityType } from '@refly/openapi-schema';
import cn from 'classnames';
import { useActionResultStoreShallow } from '@refly/stores';
import { TbArrowBackUp } from 'react-icons/tb';
import { useTranslation } from 'react-i18next';
import { getShareLink } from '@refly-packages/ai-workspace-common/utils/share';
import { copyToClipboard } from '@refly-packages/ai-workspace-common/utils';
import getClient from '@refly-packages/ai-workspace-common/requests/proxiedRequest';
import { usePublicFileUrlContext } from '@refly-packages/ai-workspace-common/context/public-file-url';
const { Paragraph } = Typography;

// Convert DriveFile type to ResourceType
const getResourceType = (fileType: string): ResourceType => {
  const typeMap: Record<string, ResourceType> = {
    'text/plain': 'file',
    'application/pdf': 'document',
    'image/jpeg': 'image',
    'image/png': 'image',
    'image/gif': 'image',
    'video/mp4': 'video',
    'audio/mpeg': 'audio',
  };
  return typeMap[fileType] || 'file';
};

type ActionButtonProps = {
  label: string;
  icon: React.ReactElement;
  loading?: boolean;
  onClick?: () => void;
  dropdownMenuItems?: MenuProps['items'];
};

const ActionButton = memo<ActionButtonProps>(
  ({ label, icon, onClick, loading, dropdownMenuItems }) => {
    const buttonNode = (
      <Button
        type="text"
        size="small"
        icon={icon}
        onClick={onClick}
        aria-label={label}
        loading={loading}
        disabled={loading}
      />
    );

    if (dropdownMenuItems?.length) {
      return (
        <Dropdown menu={{ items: dropdownMenuItems }} trigger={['click']} placement="bottomRight">
          {buttonNode}
        </Dropdown>
      );
    }

    return buttonNode;
  },
);

ActionButton.displayName = 'ActionButton';

interface ProductCardProps {
  file: DriveFile;
  classNames?: string;
  source?: 'preview' | 'card';
  onAddToFileLibrary?: (file: DriveFile) => Promise<void>;
  isAddingToFileLibrary?: boolean;
}

export const ProductCard = memo(
  ({
    file,
    classNames,
    source = 'card',
    onAddToFileLibrary,
    isAddingToFileLibrary = false,
  }: ProductCardProps) => {
    const { setCurrentFile } = useActionResultStoreShallow((state) => ({
      setCurrentFile: state.setCurrentFile,
    }));
    const inheritedUsePublicFileUrl = usePublicFileUrlContext();
    const { t } = useTranslation();

    const title = file?.name ?? 'Untitled file';

    const isMediaFile = useMemo(() => {
      return (
        getResourceType(file.type) === 'image' ||
        getResourceType(file.type) === 'video' ||
        getResourceType(file.type) === 'audio'
      );
    }, [file.type]);

    const handlePreview = useCallback(() => {
      setCurrentFile(file, { usePublicFileUrl: inheritedUsePublicFileUrl });
    }, [file, inheritedUsePublicFileUrl, setCurrentFile]);

    const [isSharing, setIsSharing] = useState(false);
    const [isAdding, setIsAdding] = useState(false);

    const handleShare = useCallback(async () => {
      if (!file?.fileId) {
        return;
      }

      setIsSharing(true);
      const loadingMessage = message.loading(t('driveFile.sharing', 'Sharing file...'), 0);

      try {
        const { data, error } = await getClient().createShare({
          body: {
            entityId: file.fileId,
            entityType: 'driveFile' as EntityType,
          },
        });

        if (!data?.success || error) {
          throw new Error(error ? String(error) : 'Failed to share file');
        }

        const shareLink = getShareLink('driveFile', data.data?.shareId ?? '');
        copyToClipboard(shareLink);
        loadingMessage();
        message.success(
          t('driveFile.shareSuccess', 'File shared successfully! Link copied to clipboard.'),
        );
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to share file:', err);
        loadingMessage();
        message.error(t('driveFile.shareError', 'Failed to share file'));
      } finally {
        setIsSharing(false);
      }
    }, [file?.fileId, t]);

    const handleAddToFileLibrary = useCallback(async () => {
      if (!onAddToFileLibrary || isAdding || isAddingToFileLibrary) {
        return;
      }

      setIsAdding(true);
      try {
        // Ensure platform-generated documents have .md extension for preview support
        const fileWithExtension = {
          ...file,
          name: file.name?.includes('.') ? file.name : `${file.name || t('common.untitled')}.md`,
        };
        await onAddToFileLibrary(fileWithExtension);
      } catch (error) {
        // Error handling is done in the parent component
        console.error('Failed to add file to library:', error);
        // Re-throw to let parent component handle the error
        throw error;
      } finally {
        setIsAdding(false);
      }
    }, [onAddToFileLibrary, file, isAdding, isAddingToFileLibrary, t]);

    const actions = useMemo<ActionButtonProps[]>(() => {
      const baseShareAction: ActionButtonProps | null = !isMediaFile
        ? {
            label: 'Share',
            icon: <Share size={16} />,
            onClick: handleShare,
            loading: isSharing,
          }
        : null;

      const addToFileLibraryAction: ActionButtonProps | null = onAddToFileLibrary
        ? {
            label: 'Add to File Library',
            icon: <LuLayoutGrid size={16} />,
            onClick: handleAddToFileLibrary,
            loading: isAdding || isAddingToFileLibrary,
          }
        : null;

      return [addToFileLibraryAction, baseShareAction].filter(
        (action): action is ActionButtonProps => Boolean(action),
      );
    }, [
      handleShare,
      handleAddToFileLibrary,
      isMediaFile,
      isSharing,
      isAdding,
      isAddingToFileLibrary,
      onAddToFileLibrary,
    ]);

    const handleClosePreview = useCallback(() => {
      setCurrentFile(null);
    }, [setCurrentFile]);

    return (
      <div
        className={cn(
          classNames,
          'overflow-hidden flex flex-col',
          source === 'card' ? 'rounded-lg border-[1px] border-solid border-refly-Card-Border' : '',
        )}
        style={
          source === 'card'
            ? {
                contentVisibility: 'auto',
                containIntrinsicSize: '0 300px',
              }
            : undefined
        }
      >
        <div className="w-full px-3 py-4 flex justify-between">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {source === 'preview' && (
              <Button
                type="text"
                size="small"
                icon={<TbArrowBackUp className="text-refly-text-0 w-5 h-5" />}
                onClick={handleClosePreview}
              />
            )}
            <NodeIcon type="file" filename={title} fileType={file.type} filled={false} small />
            <Paragraph
              className="!m-0 text-sm font-semibold flex-1 min-w-0"
              ellipsis={{
                rows: 1,
                tooltip: {
                  title: <div className="max-h-[200px] overflow-y-auto">{title}</div>,
                  placement: 'left',
                  arrow: false,
                },
              }}
            >
              {title}
            </Paragraph>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            {actions.map(
              (action) =>
                action && (
                  <ActionButton
                    key={action.label}
                    icon={action.icon}
                    label={action.label}
                    onClick={action.onClick}
                    loading={action.loading}
                    dropdownMenuItems={action.dropdownMenuItems}
                  />
                ),
            )}
          </div>
        </div>

        <div
          className={cn(
            'relative w-full px-3 pb-3 overflow-y-auto group',
            !isMediaFile && source === 'card'
              ? 'max-h-[240px] !overflow-hidden relative'
              : 'h-full',
          )}
        >
          <FilePreview
            file={file}
            markdownClassName="text-sm min-h-[50px]"
            source={source}
            onPreview={handlePreview}
          />
        </div>
      </div>
    );
  },
);

ProductCard.displayName = 'ProductCard';
